import React, { useEffect, useState, Fragment } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, mixed, date } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { CheckboxInput, TextInput, SelectInput, RadioInput } from '../../../../Helpers/Inputs';
import { InputMaskPhone, InputMaskSSN, InputMaskDate } from '../../../../Helpers/Formatters';
import moment from 'moment';
import States from '../../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../../Helpers/Lists/StatesWithDisabled';
import BackNext from '../../../Common/SubComponents/BackNext';
import IsJuvenile from '../../../../Helpers/Dates/IsJuvenile';
import GenderButtons from '../../../Common/SubComponents/GenderButtons';
import { emptyStringToUndefined } from '../../../../Helpers/Formatters/EmptyStringToUndefined.js';
import { customValidators } from '../../../../Helpers/Validators/customValidators'
import Age from '../../../../Helpers/Dates/Age'
import { sub } from 'date-fns';
export default function InsuredInfo(props) {
  const isJuvenile = IsJuvenile(props.basicInfo.birthdate);
  var currentAge = Age(props.basicInfo.birthdate);
  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: InsuredInfo.ValidationSchema(currentAge),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'state' });
    setValue('state', props.formState.state || props.basicInfo.state);

    register({ name: 'driversLicenseState' });
    setValue('driversLicenseState', props.formState.driversLicenseState);

    register({ name: 'isJuvenile' });
    setValue('isJuvenile', isJuvenile);

    register({ name: 'ownerGender' });
    setValue('ownerGender', props.formState.ownerGender);

    register({ name: 'ownerBirthdate' });
    setValue('ownerBirthdate', props.formState.ownerBirthdate);

  }, [register]);

  useEffect(() => {
    var form = { ...props.fullFormState };
    form.pages.insuredInfo.email = props.formState.email || props.basicInfo.email;
    form.pages.insuredInfo.state = props.formState.state || props.basicInfo.state;

    props.onMultiFieldChange(form);
  }, [])
  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  const [validFields, setValidFields] = useState({
    firstName: false,
    lastName: false,
    state: false,
    phone: false,
    email: false,
  });

  const validateForm = async (field) => {
    setValidFields({
      ...validFields, 
      [field]: await triggerValidation(field),
    });
  }

  useEffect(() => {
    if (Object.values(validFields).reduce((a, b) => a && b, true) && props.onFormComplete) {
      props.onFormComplete();
    }
  }, [validFields]);

  const handleChangeState = (field, value, driversLicense) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
    if (driversLicense !== "") {
      triggerValidation('driversLicense');
    }
  };

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={5}>
          <form className="quote-form" onSubmit={handleSubmit(props.onNext)}>
            <Form.Group>
              <CheckboxInput
                name="agreeToTerms"
                id="agreeToTerms"
                checked={props.formState.agreeToTerms}
                onChange={(e) => props.onFieldChange('agreeToTerms', e.target.checked)}
                errors={errors.agreeToTerms}
                inputRef={register}
                label="By starting your application you have reviewed our privacy policy and have read and understand our privacy policy and agree to its terms and conditions."
              />
              <a href="/documents/SecurianPrivacyPolicy.pdf" target="_blank">
                Privacy Policy
              </a>
            </Form.Group>

            <div className="formPageTitle">{isJuvenile === true ? "Owner Information (Parent/Guardian)" : "Owner/Proposed Insured Information"}</div>

            <Form.Group>
              <TextInput
                name="firstName"
                id="firstName"
                label="First Name"
                value={props.formState.firstName}
                onChange={(e) => props.onFieldChange('firstName', e.target.value)}
                onBlur={() => validateForm("firstName")}
                errors={errors.firstName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="middleName"
                id="middleName"
                label="Middle Name"
                value={props.formState.middleName}
                onChange={(e) => props.onFieldChange('middleName', e.target.value)}
                errors={errors.middleName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="lastName"
                id="lastName"
                label="Last Name"
                value={props.formState.lastName}
                onChange={(e) => props.onFieldChange('lastName', e.target.value)}
                onBlur={() => validateForm("lastName")}
                errors={errors.lastName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="streetAddress"
                id="streetAddress"
                label="Street Address"
                value={props.formState.streetAddress}
                onChange={(e) => props.onFieldChange('streetAddress', e.target.value)}
                errors={errors.streetAddress}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="apartment"
                id="apartment"
                label="Apt or Suite (Optional)"
                value={props.formState.apartment}
                onChange={(e) => props.onFieldChange('apartment', e.target.value)}
                errors={errors.apartment}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="city"
                id="city"
                label="City"
                value={props.formState.city}
                onChange={(e) => props.onFieldChange('city', e.target.value)}
                errors={errors.city}
                inputRef={register}
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group>
                  <SelectInput
                    name="state"
                    id="state"
                    value={props.formState.state}
                    onChange={(e) => handleValueChange('state', e.target.value)}
                    onBlur={() => validateForm("state")}
                    label="State"
                    selectOptions={StatesWithDisabled(props.unavailableStates)}
                    errors={errors && errors.state}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <TextInput
                    name="zip"
                    id="zip"
                    label="Zip"
                    value={props.formState.zip}
                    onChange={(e) => props.onFieldChange('zip', e.target.value)}
                    errors={errors.zip}
                    inputRef={register}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <TextInput
                name="phone"
                id="phone"
                label="Daytime Phone"
                value={props.formState.phone}
                onChange={(e) => props.onFieldChange('phone', e.target.value)}
                onBlur={() => validateForm("phone")}
                errors={errors.phone}
                inputRef={register}
                InputProps={{
                  inputComponent: InputMaskPhone,
                }}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="socialSecurityNumber"
                id="socialSecurityNumber"
                label="Social Security Number"
                value={props.formState.socialSecurityNumber}
                onChange={(e) => props.onFieldChange('socialSecurityNumber', e.target.value)}
                errors={errors.socialSecurityNumber}
                inputRef={register}
                InputProps={{
                  inputComponent: InputMaskSSN,
                }}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="email"
                id="email"
                label="Email"
                value={props.formState.email}
                onChange={(e) => props.onFieldChange('email', e.target.value)}
                onBlur={() => validateForm("email")}
                errors={errors.email}
                inputRef={register}
              />
            </Form.Group>

            {isJuvenile ? (
              <Fragment>
                <GenderButtons
                  selectedGender={props.formState.ownerGender}
                  onClickMale={() => handleValueChange('ownerGender', 'M')}
                  onClickFemale={() => handleValueChange('ownerGender', 'F')}
                  error={errors.ownerGender} />
                <Form.Group>
                  <TextInput
                    name="ownerBirthdate"
                    id="ownerBirthdate"
                    label="Date of Birth"
                    value={props.formState.ownerBirthdate}
                    errors={errors && errors.ownerBirthdate}
                    inputRef={props.register}
                    onChange={(e) => handleValueChange('ownerBirthdate', e.target.value)}
                    onBlur={() => validateForm("ownerBirthdate")}
                    InputProps={{
                      inputComponent: InputMaskDate,
                    }}
                  />
                </Form.Group>
              </Fragment>
            ) : (
                <Fragment>
                  <Form.Group>
                    <SelectInput
                      name="driversLicenseState"
                      id="driversLicenseState"
                      value={props.formState.driversLicenseState}
                      onChange={(e) => handleChangeState('driversLicenseState', e.target.value)}
                      label="Driver's License State"
                      selectOptions={States()}
                      errors={errors && errors.driversLicenseState}
                      inputRef={register}
                    />
                  </Form.Group>
                  <Row>
                    <Col className="col-xl-6 col-md-12 col-sm-12 col-12">
                      <Form.Group>
                        <TextInput
                          name="driversLicense"
                          id="driversLicense"
                          label="Driver's License Number"
                          value={props.formState.driversLicense}
                          onChange={(e) => handleValueChange('driversLicense', e.target.value)}
                          errors={errors.driversLicense}
                          inputRef={register}
                          disabled={props.formState.driversLicenseState === ""}
                        />
                      </Form.Group>
                    </Col>
                    <Col className="col-xl-6 col-md-12 col-sm-12 col-12">
                      <Form.Group>
                        <TextInput
                          name="driversLicenseExpiration"
                          id="driversLicenseExpiration"
                          label="Expiration Date"
                          value={props.formState.driversLicenseExpiration}
                          onChange={(e) => props.onFieldChange('driversLicenseExpiration', e.target.value)}
                          errors={errors.driversLicenseExpiration}
                          inputRef={register}
                          InputProps={{
                            inputComponent: InputMaskDate,
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Fragment>
              )}
            {isJuvenile === true && (
              <Fragment>
                <div className="formPageTitle">Proposed Insured Information (Juvenile)</div>
                <Form.Group>
                  <TextInput
                    name="childFirstName"
                    id="childFirstName"
                    label="First Name"
                    value={props.formState.childFirstName}
                    onChange={(e) => props.onFieldChange('childFirstName', e.target.value)}
                    errors={errors.childFirstName}
                    inputRef={register}
                  />
                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="childMiddleName"
                    id="childMiddleName"
                    label="Middle Name"
                    value={props.formState.childMiddleName}
                    onChange={(e) => props.onFieldChange('childMiddleName', e.target.value)}
                    errors={errors.childMiddleName}
                    inputRef={register}
                  />
                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="childLastName"
                    id="childLastName"
                    label="Last Name"
                    value={props.formState.childLastName}
                    onChange={(e) => props.onFieldChange('childLastName', e.target.value)}
                    errors={errors.childLastName}
                    inputRef={register}
                  />
                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="childSocialSecurityNumber"
                    id="childSocialSecurityNumber"
                    label="Social Security Number"
                    value={props.formState.childSocialSecurityNumber}
                    onChange={(e) => props.onFieldChange('childSocialSecurityNumber', e.target.value)}
                    errors={errors.childSocialSecurityNumber}
                    inputRef={register}
                    InputProps={{
                      inputComponent: InputMaskSSN,
                    }}
                  />
                </Form.Group>

                <Form.Group>
                  <RadioInput
                    name="legalGuardian"
                    id="legalGuardian"
                    label="Is the child a natural born child, adopted child, step-child or under legal custody or guardianship of owner?"
                    value={props.formState.legalGuardian}
                    radios={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    onChange={(e) => props.onFieldChange('legalGuardian', e.target.value)}
                    errors={errors.legalGuardian}
                    inputRef={register}
                  />
                </Form.Group>
              </Fragment>
            )}
            <BackNext onBack={props.onBack} />
          </form>
        </Col>
      </Row>
    </Container>
  );
}

InsuredInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

InsuredInfo.ValidationSchema = (currentAge) => {
  return object().shape({
    agreeToTerms: mixed().oneOf([true], 'You must agree to the privacy policy.'),
    firstName: customValidators.string().isValidNameLimitedSpecialChar('First Name is required.', true, false),
    middleName: customValidators.string().isValidNameLimitedSpecialChar('', false, false),
    lastName: customValidators.string().isValidNameLimitedSpecialChar('Last Name is required.', true, false),
    streetAddress: customValidators.string().isValidNameLimitedSpecialChar('Street Address is required.', true, true),
    apartment: customValidators.string().isValidNameLimitedSpecialChar('', false, true),
    city: customValidators.string().isValidNameLimitedSpecialChar('City is required', true, false),
    state: string().required('State is required.'),
    zip: string()
      .required('Zip code is required.')
      .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
    phone: string()
      .required('Phone number is required.')
      .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
    email: string().required('Email address is required.').email('Email address must be valid.'),
    driversLicense: string().when('isJuvenile', {
      is: false,
      then: string().when("driversLicenseState", (driversLicenseState) => {
        return customValidators.string().ValidateDriversLicense('Invalid format for selected state.', driversLicenseState).required("Driver's License Number is required.")
      }),
      otherwise: string().notRequired()
    }),
    driversLicenseState: string().when('isJuvenile', {
      is: false,
      then: string().required("Driver's License State is required."),
      otherwise: string().notRequired()
    }),
    driversLicenseExpiration: date().when('isJuvenile', {
      is: false,
      then: date()
        .transform(emptyStringToUndefined)
        .typeError('Invalid date.')
        .min(moment(new Date()).subtract(1, 'days'), 'Expiration date cannot be the in past.')
        .required('Expiration date is required'),
      otherwise: date().transform(emptyStringToUndefined).notRequired()
    }),
    socialSecurityNumber: string()
      .required('Social Security Number is required.')
      .matches(/\d{3}-\d{2}-\d{4}/, 'Social Security Number must be formatted as "123-45-6789".'),

    ownerBirthdate: date().when('isJuvenile', {
      is: true,
      then: date()
        .transform(emptyStringToUndefined)
        .typeError('Invalid date.')
        .required('Date of Birth is required.')
        .max(sub(new Date(), { years: currentAge + 1 }), `Owner's age cannot be smaller than child.`)
        .required('Birthdate is required'),
      otherwise: date().transform(emptyStringToUndefined).notRequired()
    }),
    ownerGender: string().when('isJuvenile', {
      is: true,
      then: string().required('Gender is required.'),
      otherwise: string().notRequired()
    }),
    childFirstName: string().when('isJuvenile', {
      is: true,
      then: customValidators.string().isValidNameLimitedSpecialChar('First Name is required.', true, false),
      otherwise: string().notRequired()
    }),
    childMiddleName: string().when('isJuvenile', {
      is: true,
      then: customValidators.string().isValidNameLimitedSpecialChar('Middle Name is required.', true, false),
      otherwise: string().notRequired()
    }),
    childLastName: string().when('isJuvenile', {
      is: true,
      then: customValidators.string().isValidNameLimitedSpecialChar('Last Name is required.', true, false),
      otherwise: string().notRequired()
    }),
    childSocialSecurityNumber: string().when('isJuvenile', {
      is: true,
      then: string()
        .required('Social Security Number is required.')
        .matches(/\d{3}-\d{2}-\d{4}/, 'Social Security Number must be formatted as "123-45-6789".'),
      otherwise: string().notRequired()
    }),
    legalGuardian: string().when('isJuvenile', {
      is: true,
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
  });
};

InsuredInfo.DefaultFormState = (prefill = false) => {
  return {
    agreeToTerms: prefill ? true : false,
    pin: '0000',
    firstName: prefill ? 'Reymundo' : '',
    middleName: prefill ? 'J' : '',
    lastName: prefill ? 'MATCH' + makeid(10) : '',
    streetAddress: prefill ? '123 Main Street' : '',
    apartment: prefill ? '#123' : '',
    city: prefill ? 'Boston' : '',
    state: prefill ? 'MA' : '',
    zip: prefill ? '02110' : '',
    phone: prefill ? '(123) 345-4567' : '',
    email: prefill ? 'reymundo.puga@example.com' : '',
    driversLicense: prefill ? '123a4567b' : '',
    driversLicenseState: prefill ? 'MA' : '',
    driversLicenseExpiration: prefill ? '05/05/2026' : '',
    socialSecurityNumber: prefill ? '123-12-1234' : '',
    ownerBirthdate: prefill ? '11/15/1988' : '',
    ownerGender: prefill ? 'M' : '',
    childFirstName: prefill ? 'Junior' : '',
    childMiddleName: prefill ? 'R' : '',
    childLastName: prefill ? 'MATCHjunior' + makeid(10) : '',
    childSocialSecurityNumber: prefill ? '123-02-1239' : '',
    legalGuardian: prefill ? 'true' : '',
  };
};

function makeid(l) {
  var text = "";
  var char_list = "abcdefghijklmnopqrstuvwxyz";
  for (var i = 0; i < l; i++) {
    text += char_list.charAt(Math.floor(Math.random() * char_list.length));
  }
  return text;
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Securian/WL/steps/InsuredInfo.js